import { User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, getFirestore } from 'firebase/firestore';
import { FirebaseApp } from 'firebase/app';
import { User } from './types';
import { UserBio } from '../types';

// Function to get Firestore instance with app
const getFirestoreInstance = (app?: FirebaseApp) => {
  return app ? getFirestore(app) : getFirestore();
};

export const createUserProfile = async (
  firebaseUser: FirebaseUser,
  app?: FirebaseApp,
): Promise<User> => {
  const db = getFirestoreInstance(app);
  const userData: Omit<User, 'id'> = {
    email: firebaseUser.email!,
    displayName: firebaseUser.displayName || undefined,
    photoURL: firebaseUser.photoURL || undefined,
    emailVerified: firebaseUser.emailVerified,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const userRef = doc(db, 'users', firebaseUser.uid);
  await setDoc(userRef, userData);

  return {
    id: firebaseUser.uid,
    ...userData,
  };
};

export const getUserProfile = async (userId: string, app?: FirebaseApp): Promise<User | null> => {
  const db = getFirestoreInstance(app);
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return {
      id: userSnap.id,
      ...userSnap.data(),
    } as User;
  }

  return null;
};

export const updateUserProfile = async (
  userId: string,
  updates: Partial<User>,
  app?: FirebaseApp,
): Promise<void> => {
  const db = getFirestoreInstance(app);
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: new Date(),
  });
};

export const markOnboardingCompleted = async (userId: string, app?: FirebaseApp): Promise<void> => {
  const updates: Partial<User> = {
    onboardingCompleted: true,
    updatedAt: new Date(),
  };

  await updateUserProfile(userId, updates, app);
};

export const saveUserBio = async (
  userId: string,
  userBio: UserBio,
  app?: FirebaseApp,
): Promise<void> => {
  const db = getFirestoreInstance(app);
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    userBio,
    updatedAt: new Date(),
  });
};
